import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import { PortalCommunicationSentFilterSettingStatus } from "@/Common/Models/UI/Enums";
import { IPortalCommunicationInboxFilterSetting, IPortalCommunicationSentFilterSetting } from "@/Communications/Api/Dto/PortalCommunication/IPortalCommunicationFilterSettings";

type PortalCommunicationFilterSettingsType = PortalCommunicationInboxFilterSetting | PortalCommunicationSentFilterSetting;

abstract class PortalCommunicationFilterSetting {
    public abstract dateFrom: Date;
    public abstract dateTo: Date;
    public abstract priorities: SharedItemPriority[];
    public abstract sharedWithClinicianSearchText: string;
    public abstract sharedWithClinicianContactIds: string[];
    public abstract queryPageSize: number;
    public abstract isAllSharedWithChecked: boolean;
    public abstract isAllPrioritiesChecked: boolean;
}

const defaultPortalCommunicationFilterSettingQuerySize = 50;

class PortalCommunicationInboxFilterSetting implements PortalCommunicationFilterSetting {
    public dateFrom: Date = null;
    public dateTo: Date = null;
    public priorities: SharedItemPriority[] = [];
    public sharedWithClinicianSearchText: string = "";
    public sharedWithClinicianContactIds: string[] = [];
    public queryPageSize: number = defaultPortalCommunicationFilterSettingQuerySize;
    public categoryIds: string[] = [];
    public isClinicInitiatedChecked: boolean = false;
    public isAllSharedWithChecked: boolean = false;
    public isAllPrioritiesChecked: boolean = false;
    public isAllCategoryChecked: boolean = false;

    constructor(init?: Partial<PortalCommunicationInboxFilterSetting>) {
        Object.assign(this, init);
    }

    public static toResponseModel(setting: PortalCommunicationInboxFilterSetting, omitDate: boolean = false): IPortalCommunicationInboxFilterSetting {
        const model = setting ?? new PortalCommunicationInboxFilterSetting();
        return {
            DateFrom: omitDate ? null : model.dateFrom,
            DateTo: omitDate ? null : model.dateTo,
            Priorities: model.priorities,
            SharedWithClinicianSearchText: model.sharedWithClinicianSearchText,
            SharedWithClinicianContactIds: model.sharedWithClinicianContactIds ?? [],
            CategoryIds: model.categoryIds ?? [],
            QueryPageSize: model.queryPageSize,
            IsClinicInitiatedChecked: model.isClinicInitiatedChecked,
            IsAllSharedWithChecked: model.isAllSharedWithChecked,
            IsAllPrioritiesChecked: model.isAllPrioritiesChecked,
            IsAllCategoryChecked: model.isAllCategoryChecked
        };
    }

    public static fromResponseModel(response: IPortalCommunicationInboxFilterSetting): PortalCommunicationInboxFilterSetting {
        if (!response) {
            return new PortalCommunicationInboxFilterSetting();
        }

        const model = new PortalCommunicationInboxFilterSetting();
        model.dateFrom = response.DateFrom;
        model.dateTo = response.DateTo;
        model.priorities = response.Priorities;
        model.sharedWithClinicianSearchText = response.SharedWithClinicianSearchText;
        model.sharedWithClinicianContactIds = response.SharedWithClinicianContactIds ?? [];
        model.categoryIds = response.CategoryIds ?? [];
        model.queryPageSize = response.QueryPageSize;
        model.isClinicInitiatedChecked = response.IsClinicInitiatedChecked;
        model.isAllSharedWithChecked = response.IsAllSharedWithChecked;
        model.isAllPrioritiesChecked = response.IsAllPrioritiesChecked;
        model.isAllCategoryChecked = response.IsAllCategoryChecked;
        return model;
    }
}

class PortalCommunicationSentFilterSetting implements PortalCommunicationFilterSetting {
    public dateFrom: Date = null;
    public dateTo: Date = null;
    public priorities: SharedItemPriority[] = [];
    public sharedWithClinicianSearchText: string = "";
    public sharedWithClinicianContactIds: string[] = [];
    public queryPageSize: number = 50;
    public statuses: PortalCommunicationSentFilterSettingStatus[] = [];
    public isAllSharedWithChecked: boolean = false;
    public isAllPrioritiesChecked: boolean = false;
    public isAllStatusChecked: boolean = false;

    constructor(init?: Partial<PortalCommunicationInboxFilterSetting>) {
        Object.assign(this, init);
    }

    public static toResponseModel(setting: PortalCommunicationSentFilterSetting, omitDate: boolean = false): IPortalCommunicationSentFilterSetting {
        const model = setting ?? new PortalCommunicationSentFilterSetting();
        return {
            DateFrom: omitDate ? null : model.dateFrom,
            DateTo: omitDate ? null : model.dateTo,
            Priorities: model.priorities,
            SharedWithClinicianSearchText: model.sharedWithClinicianSearchText,
            SharedWithClinicianContactIds: model.sharedWithClinicianContactIds ?? [],
            Statuses: model.statuses,
            QueryPageSize: model.queryPageSize,
            IsAllSharedWithChecked: model.isAllSharedWithChecked,
            IsAllPrioritiesChecked: model.isAllPrioritiesChecked,
            IsAllStatusChecked: model.isAllStatusChecked
        };
    }

    public static fromResponseModel(response: IPortalCommunicationSentFilterSetting): PortalCommunicationSentFilterSetting {
        if (!response) {
            return new PortalCommunicationSentFilterSetting();
        }

        const model = new PortalCommunicationSentFilterSetting();
        model.dateFrom = response.DateFrom;
        model.dateTo = response.DateTo;
        model.priorities = response.Priorities;
        model.sharedWithClinicianSearchText = response.SharedWithClinicianSearchText;
        model.sharedWithClinicianContactIds = response.SharedWithClinicianContactIds ?? [];
        model.statuses = response.Statuses;
        model.queryPageSize = response.QueryPageSize;
        model.isAllSharedWithChecked = response.IsAllSharedWithChecked;
        model.isAllPrioritiesChecked = response.IsAllPrioritiesChecked;
        model.isAllStatusChecked = response.IsAllStatusChecked;
        return model;
    }
}

export { defaultPortalCommunicationFilterSettingQuerySize, PortalCommunicationFilterSettingsType, PortalCommunicationInboxFilterSetting, PortalCommunicationSentFilterSetting };
